
import { useEffect, useState } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { DataTable, Column } from "@/components/ui/data-table"
import { courseService } from "@/services/course.service"
import { Author } from "@/types/api"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Edit2, Trash2, Plus, Loader2 } from "lucide-react"
import { AuthorDialog } from "@/components/courses/AuthorDialog"
import { useToast } from "@/hooks/use-toast"
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog"

export default function AuthorsPage() {
    const [authors, setAuthors] = useState<Author[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [page, setPage] = useState(1)
    const [totalCount, setTotalCount] = useState(0)
    const [editAuthor, setEditAuthor] = useState<Author | null>(null)
    const [isDialogOpen, setIsDialogOpen] = useState(false)
    const [deleteAuthorSlug, setDeleteAuthorSlug] = useState<string | null>(null)
    const [isDeleting, setIsDeleting] = useState(false)

    const { toast } = useToast()
    const PAGE_SIZE = 10

    const fetchAuthors = async () => {
        setIsLoading(true)
        try {
            const response = await courseService.getAuthors({
                limit: PAGE_SIZE,
                offset: (page - 1) * PAGE_SIZE,
            })
            setAuthors(response.results)
            setTotalCount(response.count)
        } catch (error) {
            console.error(error)
            toast({
                title: "Ошибка",
                description: "Не удалось загрузить авторов",
                variant: "destructive",
            })
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        fetchAuthors()
    }, [page])

    const handleDelete = async () => {
        if (!deleteAuthorSlug) return
        setIsDeleting(true)
        try {
            await courseService.deleteAuthor(deleteAuthorSlug)
            toast({
                title: "Успешно",
                description: "Автор успешно удален",
            })
            fetchAuthors()
        } catch (error) {
            console.error(error)
            toast({
                title: "Ошибка",
                description: "Не удалось удалить автора",
                variant: "destructive",
            })
        } finally {
            setIsDeleting(false)
            setDeleteAuthorSlug(null)
        }
    }

    const handleEdit = (author: Author) => {
        setEditAuthor(author)
        setIsDialogOpen(true)
    }

    const handleCreate = () => {
        setEditAuthor(null)
        setIsDialogOpen(true)
    }

    const columns: Column<Author>[] = [
        {
            header: "Автор",
            cell: (author) => (
                <div className="flex items-center gap-3">
                    <Avatar className="h-10 w-10 rounded-md">
                        <AvatarImage src={author.image || undefined} className="rounded-md object-cover" />
                        <AvatarFallback className="rounded-md">{author.full_name?.charAt(0) || "A"}</AvatarFallback>
                    </Avatar>
                    <div className="font-medium">{author.full_name}</div>
                </div>
            ),
        },
        {
            header: "Биография",
            cell: (author) => <div className="truncate max-w-md" title={author.bio}>{author.bio}</div>,
        },
        {
            header: "Действия",
            className: "text-right",
            cell: (author) => (
                <div className="flex justify-end gap-2">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(author)}
                    >
                        <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="text-red-500 hover:text-red-700"
                        onClick={() => setDeleteAuthorSlug(author.slug)}
                    >
                        <Trash2 className="h-4 w-4" />
                    </Button>
                </div>
            ),
        },
    ]

    return (
        <div>
            <div className="mb-8 flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">Авторы</h1>
                    <p className="text-gray-600 mt-1">Управление авторами курсов.</p>
                </div>
                <Button onClick={handleCreate}>
                    <Plus className="mr-2 h-4 w-4" />
                    Создать автора
                </Button>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle>Список авторов</CardTitle>
                </CardHeader>
                <CardContent>
                    <DataTable
                        columns={columns}
                        data={authors}
                        isLoading={isLoading}
                        pageCount={Math.ceil(totalCount / PAGE_SIZE)}
                        currentPage={page}
                        onPageChange={setPage}
                    />
                </CardContent>
            </Card>

            <AuthorDialog
                author={editAuthor}
                open={isDialogOpen}
                onOpenChange={setIsDialogOpen}
                onSuccess={fetchAuthors}
            />

            <AlertDialog open={!!deleteAuthorSlug} onOpenChange={(open) => !open && setDeleteAuthorSlug(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Вы уверены?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Это действие нельзя отменить.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={isDeleting}>Отмена</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} disabled={isDeleting} className="bg-red-600 hover:bg-red-700">
                            {isDeleting ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Удаление...
                                </>
                            ) : (
                                "Удалить"
                            )}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    )
}
